import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useFinancial } from '../context/FinancialContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiDollarSign, FiShield, FiAlertTriangle, FiTrendingUp, FiPercent, FiActivity, FiBriefcase, FiHome, FiCreditCard } = FiIcons;

// Moved outside to prevent re-mounting and focus loss on every state change
const SectionTitle = ({ icon, title, subtitle }) => (
  <div className="flex items-center space-x-3 mb-6">
    <div className="w-10 h-10 rounded-lg bg-[#19b48d]/10 flex items-center justify-center">
      <SafeIcon icon={icon} className="text-[#19b48d] text-xl" />
    </div>
    <div>
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  </div>
);

// Utility function to safely evaluate mathematical expressions
const evaluateExpression = (expression) => {
  if (!expression || expression === '') return 0;
  
  // Convert to string and clean up
  const expr = String(expression).trim();
  
  // If it's already a number, return it
  if (!isNaN(expr) && expr !== '') {
    return parseFloat(expr);
  }
  
  try {
    // Only allow numbers, +, -, *, /, (, ), and whitespace
    if (!/^[\d\s+\-*/().]+$/.test(expr)) {
      return 0;
    }
    
    // Evaluate the expression safely
    // Using Function constructor instead of eval for better security
    const result = Function('"use strict"; return (' + expr + ')')();
    
    // Check if result is a valid number
    if (isNaN(result) || !isFinite(result)) {
      return 0;
    }
    
    return Math.round(result * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    // If evaluation fails, return 0
    return 0;
  }
};

// Moved outside to prevent re-mounting and focus loss on every state change
const InputField = ({ label, value, onChange, type = "text", prefix = "₹", hint }) => {
  const [displayValue, setDisplayValue] = useState(value || '');
  const [isCalculating, setIsCalculating] = useState(false);

  // Update display value when prop value changes
  React.useEffect(() => {
    setDisplayValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    
    // Check if input contains mathematical operators
    if (inputValue.includes('+') || inputValue.includes('-') || inputValue.includes('*') || inputValue.includes('/')) {
      setIsCalculating(true);
    } else {
      setIsCalculating(false);
    }
  };

  const handleBlur = () => {
    if (type === "number" || type === "text") {
      // Evaluate the expression
      const calculatedValue = evaluateExpression(displayValue);
      
      // Update the display to show calculated result
      setDisplayValue(calculatedValue);
      
      // Call onChange with the calculated number
      onChange(calculatedValue);
      
      setIsCalculating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Prevent the native form submission that Enter would otherwise
      // trigger (which was jumping the user to the next page) — just
      // calculate and move focus off the field instead.
      e.preventDefault();
      e.target.blur();
    }
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">
          {prefix}
        </span>
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`w-full pl-8 pr-4 py-2.5 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent transition-all outline-none text-sm font-medium ${
            isCalculating ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
          }`}
          placeholder={type === "text" ? "" : "Enter value or calculation (e.g., 100000+200000)"}
        />
      </div>
      {/* Calculation status is shown below the field only, never overlaid on
          top of it, so it never visually mixes with what's being typed. */}
      {isCalculating ? (
        <p className="mt-1 text-[10px] text-blue-600 font-medium">
          💡 Calculating {displayValue} — press Enter or click outside to confirm
        </p>
      ) : (
        hint && <p className="mt-1 text-[10px] text-gray-400">{hint}</p>
      )}
    </div>
  );
};

function UserInputForm() {
  const { state, dispatch } = useFinancial();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    ...state.userProfile,
  });

  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    if (keys.length === 1) {
      setFormData(prev => ({
        ...prev,
        [keys[0]]: value
      }));
    } else if (keys.length === 2) {
      setFormData(prev => ({
        ...prev,
        [keys[0]]: {
          ...prev[keys[0]],
          [keys[1]]: value
        }
      }));
    }
  };

  const calculations = useMemo(() => {
    const annualIncome = (formData.monthlyIncome || 0) * 12;
    const monthlyExpenses = formData.monthlyExpenses || 0;

    // Emergency Fund
    const recEF = monthlyExpenses * 6;
    const efMonths = monthlyExpenses > 0 ? (formData.emergencyFund || 0) / monthlyExpenses : 0;

    // Insurance
    const recTerm = annualIncome * 20;
    const recHealth = Math.max(900000, annualIncome * 0.5);
    const termRatio = recTerm > 0 ? ((formData.insurance?.termInsurance || 0) / recTerm) * 100 : 0;
    const healthRatio = recHealth > 0 ? ((formData.insurance?.healthInsurance || 0) / recHealth) * 100 : 0;

    // Assets
    const totalAssets = Object.values(formData.existingInvestments || {}).reduce((a, b) => a + (Number(b) || 0), 0) +
      Object.values(formData.assets || {}).reduce((a, b) => a + (Number(b) || 0), 0);

    // Liabilities
    const totalLiabilities = Object.values(formData.liabilities || {}).reduce((a, b) => a + (Number(b) || 0), 0);

    return {
      recEF,
      efMonths,
      recTerm,
      recHealth,
      termRatio,
      healthRatio,
      totalAssets,
      totalLiabilities,
      netWorth: totalAssets - totalLiabilities
    };
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch({ type: 'UPDATE_USER_PROFILE', payload: formData });
    navigate('/investments');
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(val || 0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto pb-20"
    >
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-gray-900">Financial Details</h2>
        <p className="text-gray-500 mt-2">Comprehensive profile setup for your financial roadmap</p>
        <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200 max-w-2xl mx-auto">
          <p className="text-sm text-blue-800 font-medium">
            💡 <strong>Pro Tip:</strong> You can use calculations in number fields! 
          </p>
          <p className="text-xs text-blue-700 mt-1">
            Examples: "100000+200000+300000" or "500000-50000" - Press Enter or click outside to calculate
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal & Cashflow */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <SectionTitle icon={FiUser} title="Personal & Cashflow" subtitle="Basic income and expense details" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <InputField
              label="Current Age"
              value={formData.currentAge}
              onChange={v => handleInputChange('currentAge', v)}
              prefix=""
              type="number"
            />
            <InputField
              label="Retirement Age"
              value={formData.retirementAge}
              onChange={v => handleInputChange('retirementAge', v)}
              prefix=""
              type="number"
            />
            <InputField
              label="Monthly Income"
              value={formData.monthlyIncome}
              onChange={v => handleInputChange('monthlyIncome', v)}
            />
            <InputField
              label="Monthly Expenses"
              value={formData.monthlyExpenses}
              onChange={v => handleInputChange('monthlyExpenses', v)}
            />
            <InputField
              label="Expected Monthly Expense (Post-Retirement)"
              value={formData.postRetirementMonthlyExpense}
              onChange={v => handleInputChange('postRetirementMonthlyExpense', v)}
              hint="Used as the default monthly income target for your retirement goal"
            />
          </div>
        </div>

        {/* Emergency Fund */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <SectionTitle icon={FiAlertTriangle} title="Emergency Fund" />
            <InputField
              label="Emergency Fund Amount"
              value={formData.emergencyFund}
              onChange={v => handleInputChange('emergencyFund', v)}
              hint={`Recommended: ${formatCurrency(calculations.recEF)}`}
            />
            <div className="mt-6 p-4 bg-orange-50 rounded-xl border border-orange-100">
              <p className="text-xs font-bold text-orange-800 uppercase mb-1">Status Analysis</p>
              <p className="text-sm text-orange-700">Current Coverage: <b>{calculations.efMonths.toFixed(1)} months</b></p>
              <p className="text-[11px] text-orange-600 mt-1">Experts recommend 3-6 months of expenses for security.</p>
            </div>
          </div>

          {/* Insurance */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <SectionTitle icon={FiShield} title="Insurance Coverage" />
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label="Term Life Cover"
                value={formData.insurance?.termInsurance}
                onChange={v => handleInputChange('insurance.termInsurance', v)}
              />
              <InputField
                label="Health Cover"
                value={formData.insurance?.healthInsurance}
                onChange={v => handleInputChange('insurance.healthInsurance', v)}
              />
              <InputField
                label="Annual Term Premium"
                value={formData.insurance?.termPremium}
                onChange={v => handleInputChange('insurance.termPremium', v)}
              />
              <InputField
                label="Annual Health Premium"
                value={formData.insurance?.healthPremium}
                onChange={v => handleInputChange('insurance.healthPremium', v)}
              />
            </div>
            <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100 grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-blue-700 font-bold uppercase">Term Ratio</p>
                <p className="text-lg font-bold text-blue-900">{calculations.termRatio.toFixed(0)}%</p>
              </div>
              <div>
                <p className="text-[10px] text-blue-700 font-bold uppercase">Health Ratio</p>
                <p className="text-lg font-bold text-blue-900">{calculations.healthRatio.toFixed(0)}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Assets & Investments */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <SectionTitle icon={FiBriefcase} title="Assets & Investments" subtitle="All your current holdings - Use calculations like 100000+200000" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <InputField
              label="Fixed Deposits"
              value={formData.existingInvestments?.fixedDeposits}
              onChange={v => handleInputChange('existingInvestments.fixedDeposits', v)}
            />
            <InputField
              label="Mutual Funds"
              value={formData.existingInvestments?.mutualFunds}
              onChange={v => handleInputChange('existingInvestments.mutualFunds', v)}
            />
            <InputField
              label="Stocks/Equity"
              value={formData.existingInvestments?.stocks}
              onChange={v => handleInputChange('existingInvestments.stocks', v)}
            />
            <InputField
              label="EPF/PPF"
              value={formData.existingInvestments?.epfPpf}
              onChange={v => handleInputChange('existingInvestments.epfPpf', v)}
            />
            <InputField
              label="SSY/NSC"
              value={formData.existingInvestments?.ssyNsc}
              onChange={v => handleInputChange('existingInvestments.ssyNsc', v)}
            />
            <InputField
              label="Chit Fund"
              value={formData.existingInvestments?.chitFund}
              onChange={v => handleInputChange('existingInvestments.chitFund', v)}
            />
            <InputField
              label="Gold"
              value={formData.existingInvestments?.gold}
              onChange={v => handleInputChange('existingInvestments.gold', v)}
            />
            <InputField
              label="Silver"
              value={formData.existingInvestments?.silver}
              onChange={v => handleInputChange('existingInvestments.silver', v)}
            />
            <InputField
              label="Copper"
              value={formData.existingInvestments?.copper}
              onChange={v => handleInputChange('existingInvestments.copper', v)}
            />
            <InputField
              label="Real Estate"
              value={formData.existingInvestments?.realEstate}
              onChange={v => handleInputChange('existingInvestments.realEstate', v)}
            />
            <InputField
              label="Bank Savings"
              value={formData.assets?.bankSavings}
              onChange={v => handleInputChange('assets.bankSavings', v)}
            />
            <InputField
              label="Bonds"
              value={formData.existingInvestments?.bonds}
              onChange={v => handleInputChange('existingInvestments.bonds', v)}
            />
            <InputField
              label="PMS"
              value={formData.existingInvestments?.pms}
              onChange={v => handleInputChange('existingInvestments.pms', v)}
            />
            <InputField
              label="Crypto"
              value={formData.existingInvestments?.crypto}
              onChange={v => handleInputChange('existingInvestments.crypto', v)}
            />
            <InputField
              label="Vehicle Value"
              value={formData.assets?.vehicles}
              onChange={v => handleInputChange('assets.vehicles', v)}
            />
            <InputField
              label="Others"
              value={formData.existingInvestments?.others}
              onChange={v => handleInputChange('existingInvestments.others', v)}
            />
          </div>
        </div>

        {/* Liabilities */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <SectionTitle icon={FiCreditCard} title="Liabilities & Loans" subtitle="Current outstanding debts - Use calculations like 500000-50000" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <InputField
              label="Home Loan"
              value={formData.liabilities?.homeLoan}
              onChange={v => handleInputChange('liabilities.homeLoan', v)}
            />
            <InputField
              label="Car Loan"
              value={formData.liabilities?.carLoan}
              onChange={v => handleInputChange('liabilities.carLoan', v)}
            />
            <InputField
              label="Personal Loan"
              value={formData.liabilities?.personalLoan}
              onChange={v => handleInputChange('liabilities.personalLoan', v)}
            />
            <InputField
              label="Credit Card"
              value={formData.liabilities?.creditCard}
              onChange={v => handleInputChange('liabilities.creditCard', v)}
            />
            <InputField
              label="Education Loan"
              value={formData.liabilities?.educationLoan}
              onChange={v => handleInputChange('liabilities.educationLoan', v)}
            />
            <InputField
              label="Other Loans"
              value={formData.liabilities?.others}
              onChange={v => handleInputChange('liabilities.others', v)}
            />
          </div>
        </div>

        {/* Net Worth Summary */}
        <div className="bg-gradient-to-br from-[#19b48d] to-[#16a085] rounded-2xl p-8 text-white shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1">Total Assets</p>
              <p className="text-3xl font-bold">{formatCurrency(calculations.totalAssets)}</p>
            </div>
            <div>
              <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1">Total Liabilities</p>
              <p className="text-3xl font-bold">{formatCurrency(calculations.totalLiabilities)}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm border border-white/20">
              <p className="text-white/80 text-xs font-bold uppercase tracking-widest mb-1">Net Worth</p>
              <p className="text-4xl font-black">{formatCurrency(calculations.netWorth)}</p>
            </div>
          </div>
        </div>

        {/* Assumptions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <SectionTitle icon={FiTrendingUp} title="Return Expectations (%)" />
            <div className="space-y-4">
              <InputField
                label="Short-term (0-3 yrs)"
                value={formData.returnExpectations?.shortTerm}
                onChange={v => handleInputChange('returnExpectations.shortTerm', v)}
                prefix="%"
                hint="FD, Liquid Funds"
                type="number"
              />
              <InputField
                label="Medium-term (3-7 yrs)"
                value={formData.returnExpectations?.mediumTerm}
                onChange={v => handleInputChange('returnExpectations.mediumTerm', v)}
                prefix="%"
                hint="Hybrid, Debt Funds"
                type="number"
              />
              <InputField
                label="Long-term (7+ yrs)"
                value={formData.returnExpectations?.longTerm}
                onChange={v => handleInputChange('returnExpectations.longTerm', v)}
                prefix="%"
                hint="Equity, Stocks"
                type="number"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <SectionTitle icon={FiPercent} title="Inflation Rates (%)" />
            <div className="space-y-4">
              <InputField
                label="General Inflation"
                value={formData.inflationRates?.general}
                onChange={v => handleInputChange('inflationRates.general', v)}
                prefix="%"
                hint="Avg 6-7%"
                type="number"
              />
              <InputField
                label="Education Inflation"
                value={formData.inflationRates?.education}
                onChange={v => handleInputChange('inflationRates.education', v)}
                prefix="%"
                hint="Avg 10-12%"
                type="number"
              />
              <InputField
                label="Medical Inflation"
                value={formData.inflationRates?.medical}
                onChange={v => handleInputChange('inflationRates.medical', v)}
                prefix="%"
                hint="Avg 10-12%"
                type="number"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-8">
          <button
            type="submit"
            className="px-12 py-4 bg-[#19b48d] text-white font-bold rounded-xl hover:bg-[#16a085] hover:shadow-lg transition-all duration-200 flex items-center space-x-2 group"
          >
            <span>Continue to Investments</span>
            <SafeIcon icon={FiIcons.FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </form>
    </motion.div>
  );
}

export default UserInputForm;